import { createFileRoute } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { ChapterCard } from "~/components/chapter-card";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { Skeleton } from "~/components/ui/skeleton";
import { currentPageContextAtom } from "~/components/practice/chat-widget";
import { MATERIAL_METADATA } from "~/data/material-metadata";
import { useLearningMaterials } from "~/lib/client/learn";
import { generateLearnIndexPageContext } from "~/lib/context-gen";
import { m } from "~/i18n/messages";
import { ResourceCard } from "~/components/resource-card";
import { Separator } from "~/components/ui/separator";
import { RESOURCES } from "~/data/resources";
import { generateResourcesPageContext } from "~/lib/context-gen";

// Moved LearningMaterialsSkeleton component from src/components/skeletons/learning-materials-skeleton.tsx
function LearningMaterialsSkeleton() {
	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 py-4">
			<section className="mb-4">
				<Skeleton className="h-8 w-48 mb-2" />
				<Skeleton className="h-4 w-full mb-1" />
				<Skeleton className="h-4 w-3/4 mb-1" />
				<Skeleton className="h-4 w-full mb-1" />
				<Skeleton className="h-4 w-1/2" />
			</section>

			<section className="flex flex-col gap-2">
				{["n5", "n4", "n3", "n2", "n1"].map((level) => (
					<div
						key={`skeleton-${level}`}
						className="bg-white rounded-lg border border-pink-200 p-4"
					>
						<div className="flex items-center justify-between mb-2">
							<Skeleton className="h-6 w-24" />
							<Skeleton className="h-5 w-16" />
						</div>
						<Skeleton className="h-4 w-full mb-2" />
						<div className="flex gap-2">
							<Skeleton className="h-8 w-20" />
							<Skeleton className="h-8 w-20" />
							<Skeleton className="h-8 w-20" />
						</div>
					</div>
				))}
			</section>
		</div>
	);
}

export const Route = createFileRoute("/app/learn/")({
	component: LearnPage,
});

function LearnPage() {
	return (
		<SuspenseFallback
			loading={<LearningMaterialsSkeleton />}
			error={
				<div className="p-8 text-red-500">
					{m["errors.loading_learning_materials"]({ message: "An error occurred while loading learning materials" })}
				</div>
			}
		>
			<LearnPageContent />
		</SuspenseFallback>
	);
}

function LearnPageContent() {
	const setPageContext = useSetAtom(currentPageContextAtom);
	const { data: materialsData } = useLearningMaterials();

	const materials = materialsData
		? [
				{
					...MATERIAL_METADATA.n5,
					...(materialsData.n5 ?? {}),
				},
				{
					...MATERIAL_METADATA.n4,
					...(materialsData.n4 ?? {}),
				},
				{
					...MATERIAL_METADATA.n3,
					...(materialsData.n3 ?? {}),
				},
				{
					...MATERIAL_METADATA.n2,
					...(materialsData.n2 ?? {}),
				},
				{
					...MATERIAL_METADATA.n1,
					...(materialsData.n1 ?? {}),
				},
			]
		: [];

	useEffect(() => {
		setPageContext({
			title: m["study.title"](),
			slug: "/app/learn",
			contextData: generateLearnIndexPageContext({
				materialCount: materials.length,
			}),
		});
	}, [setPageContext, materials]);

	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 py-4">
			<section className="mb-4">
				<h1 className="text-xl sm:text-2xl font-semibold text-pink-600 mb-2">
					{m["study.title"]()}
				</h1>
				<p className="text-sm md:text-base text-muted-foreground">
					{m["study.organization_info"]()}{" "}
					<a
						className="text-pink-400 underline"
						href="https://www.jlpt.jp/e/about/levelsummary.html"
					>
						{m["study.jlpt_levels_link_text"]()}
					</a>{" "}
					{m["study.level_coverage_note"]()}
				</p>
				<p className="text-sm md:text-base text-muted-foreground">
					{m["study.grouping_info"]()}{" "}
					<a className="text-pink-400 underline" href="https://takoboto.com">
						{m["study.takoboto_link_text"]()}
					</a>{" "}
					{m["study.no_official_grouping_note"]()}
				</p>
			</section>

			<section className="flex flex-col gap-2">
				{materials.map((material) => (
					<ChapterCard key={material.level} {...material} />
				))}
			</section>

			<section className="my-8">
				<h1 className="text-xl lg:text-2xl font-semibold text-pink-600 mb-2">
					{m["resources.title"]()}
				</h1>
				<p className="text-sm lg:text-base text-muted-foreground mb-2">
					{m["resources.description"]()}
				</p>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.integration_note"]()}
				</p>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.application_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.application_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter(
						(resource) => resource.category === "application",
					).map((app) => (
						<li key={app.title}>
							<ResourceCard {...app} />
						</li>
					))}
				</ol>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.resource_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.resource_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter((resource) => resource.category === "resource").map(
						(resource) => (
							<li key={resource.title}>
								<ResourceCard {...resource} />
							</li>
						),
					)}
				</ol>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.dictionary_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.dictionary_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter(
						(resource) => resource.category === "dictionary",
					).map((resource) => (
						<li key={resource.title}>
							<ResourceCard {...resource} />
						</li>
					))}
				</ol>
			</section>
		</div>
	);
}

function ResourcesPage() {
	const setPageContext = useSetAtom(currentPageContextAtom);

	useEffect(() => {
		setPageContext({
			title: m["resources.page_title"](),
			slug: "/app/resources",
			contextData: generateResourcesPageContext({}),
		});
	}, [setPageContext]);

	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 py-4">
			<section className="mb-8">
				<h1 className="text-xl lg:text-2xl font-semibold text-pink-600 mb-2">
					{m["resources.title"]()}
				</h1>
				<p className="text-sm lg:text-base text-muted-foreground mb-2">
					{m["resources.description"]()}
				</p>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.integration_note"]()}
				</p>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.application_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.application_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter(
						(resource) => resource.category === "application",
					).map((app) => (
						<li key={app.title}>
							<ResourceCard {...app} />
						</li>
					))}
				</ol>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.resource_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.resource_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter((resource) => resource.category === "resource").map(
						(resource) => (
							<li key={resource.title}>
								<ResourceCard {...resource} />
							</li>
						),
					)}
				</ol>
			</section>
			<section className="my-8">
				<h2 className="font-medium text-pink-500 text-xl">
					{m["resources.dictionary_category"]()}
				</h2>
				<p className="text-sm lg:text-base text-muted-foreground">
					{m["resources.dictionary_description"]()}
				</p>
				<Separator className="mt-2 mb-4" />
				<ol className="flex flex-col gap-4">
					{RESOURCES.filter(
						(resource) => resource.category === "dictionary",
					).map((resource) => (
						<li key={resource.title}>
							<ResourceCard {...resource} />
						</li>
					))}
				</ol>
			</section>
		</div>
	);
}
